import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { FileText, User, Building2, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { API_BASE } from '../../config/api';

const AdminAuditLogs = () => {
  const { token } = useAuth();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [workspaceFilter, setWorkspaceFilter] = useState('');

  useEffect(() => {
    loadLogs();
  }, [actionFilter]);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (actionFilter) params.append('action_type', actionFilter);
      if (workspaceFilter) params.append('workspace_id', workspaceFilter);
      
      const response = await fetch(
        `${API_BASE}/admin/support/audit-logs?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      
      if (!response.ok) throw new Error('Failed to load audit logs');
      const data = await response.json();
      setLogs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadgeVariant = (action) => {
    switch (action) {
      case 'plan_change': return 'default';
      case 'override_created': return 'secondary';
      case 'override_deleted': return 'destructive';
      case 'member_role_changed': return 'outline';
      case 'member_removed': return 'destructive';
      case 'member_added': return 'secondary';
      default: return 'outline';
    }
  };

  const formatActionType = (action) => {
    return action.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Audit Logs</h2>
        <p className="text-muted-foreground">Track all admin actions for accountability</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Select value={actionFilter || "all"} onValueChange={(v) => setActionFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="plan_change">Plan Change</SelectItem>
                <SelectItem value="override_created">Override Created</SelectItem>
                <SelectItem value="override_deleted">Override Deleted</SelectItem>
                <SelectItem value="member_role_changed">Role Changed</SelectItem>
                <SelectItem value="member_removed">Member Removed</SelectItem>
                <SelectItem value="member_added">Member Added</SelectItem>
              </SelectContent>
            </Select>
            
            <Input
              placeholder="Filter by workspace ID..."
              value={workspaceFilter}
              onChange={(e) => setWorkspaceFilter(e.target.value)}
              className="max-w-xs"
            />
            
            <Button variant="outline" onClick={loadLogs}>
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading logs...</div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Recent Actions ({logs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length > 0 ? (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="p-4 bg-muted rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={getActionBadgeVariant(log.action_type)}>
                            {formatActionType(log.action_type)}
                          </Badge>
                        </div>
                        <p className="text-sm">
                          <span className="text-muted-foreground">Admin:</span>{' '}
                          <span className="font-medium">{log.admin_name || log.admin_id}</span>
                        </p>
                        {log.workspace_name && (
                          <p className="text-sm">
                            <span className="text-muted-foreground">Workspace:</span>{' '}
                            <span className="font-medium">{log.workspace_name}</span>
                          </p>
                        )}
                        {log.target_user_email && (
                          <p className="text-sm">
                            <span className="text-muted-foreground">Target User:</span>{' '}
                            <span className="font-medium">{log.target_user_email}</span>
                          </p>
                        )}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <pre className="bg-background p-2 rounded overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <p className="flex items-center gap-1 justify-end">
                          <Calendar className="h-3 w-3" />
                          {new Date(log.created_at).toLocaleDateString()}
                        </p>
                        <p>{new Date(log.created_at).toLocaleTimeString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-muted-foreground">
                No audit logs found
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAuditLogs;
